import {
  Button,
  Card,
  Col,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
// import {IconRefresh} from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
// import type {TableProps} from "antd";
import Icon, { ReloadOutlined } from "@ant-design/icons";
import { CiCircleCheck, CiRead, CiUnread } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiFactory from "../../../api";
import { GeneralModal } from "../../../components/modal/GeneralModal";
import { ALARM } from "../../../config/Constant";
import { CommentIcon, ProjectIcon, TaskIcon } from "../../../config/Icon";
import { useInfoUser } from "../../../store/UserStore";
import { formatDate, formatTime } from "../../../utils/formatTime";
import {openNotificationError, openNotificationWarn, truncateText} from "../../../utils/Utils";
import "../../../styles/table.scss";
import { BsCalendar2Check } from "react-icons/bs";
import { FaSquareFull } from "react-icons/fa";

const AlarmList = ({ isMobile, backBtn }) => {
  const navigate = useNavigate();
  const { user, languageMap } = useInfoUser();
  const [alarmList, setAlarmList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropListSearch, setDropListSearch] = useState({
    type: "ALL",
  });

  const [pagination, setPagination] = useState({
    pageSize: ALARM.MY_ALARM_LIMIT,
    total: 0,
    current: 1,
  });

  const listRef = useRef(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const fetchAlarmsData = async (newPagination = pagination) => {
    setLoading(true);
    try {
      const { current, pageSize } = newPagination;
      const request = {
        limit: pageSize,
        page: current,
        search: dropListSearch,
      };

      const result = await apiFactory.alarmApi.getList(request);
      if (result?.status === 200) {
        setAlarmList(result?.data?.items || []);
        setPagination((prev) => ({
          ...prev,
          total: result?.data?.totalItems || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching alarm list data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeAlarmList = async () => {
    setIsOpenDeleteModal(false);

    try {
      const result = await apiFactory.alarmApi.changeAlarmParticipantList();

      if (result?.status === 200) {
        const updatedAlarms = [...alarmList];

        result.data.forEach((item) => {
          const index = updatedAlarms.findIndex(
            (alarm) => alarm.alarmCode === item.alarmCode
          );

          if (index !== -1) {
            updatedAlarms[index].read = item.read;
          }
        });

        setAlarmList(updatedAlarms);
      } else {
        openNotificationError(result?.message);
      }
    } catch (error) {
      openNotificationError("An error occurred while changing the alarm list.");
    }
  };

  useEffect(() => {
    fetchAlarmsData();
  }, [pagination.current, pagination.pageSize, dropListSearch]);

  const handleTableChange = (pagination) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const handleSelectChange = (value) => {
    setDropListSearch((prevState) => ({
      ...prevState,
      type: value,
    }));
    setPagination({ current: 1, pageSize: 10, total: 0 });
  };

  const handleReload = () => {
    setPagination({ current: 1, pageSize: 10, total: 0 });
    setDropListSearch({ type: "ALL" });
  };

  const handleChangeRead = async (record) => {
    const result = await apiFactory.alarmApi.changeAlarmParticipant(
      record?.alarmCode
    );
    if (result?.status !== 200) {
      return openNotificationError(result?.message);
    }
    record.read = result?.data?.read;

    const alarms = alarmList.map((item) => {
      if (item.alarmCode === record.alarmCode) {
        item.read = record.read;
        return item;
      }
      return item;
    });
    setAlarmList(alarms);
  };

  const handleTimelineTaskCodeClick = (record) => {
    if (record?.type === "TASK") {
      navigate(
        `/work-management/timeline/${record.projectCode}?task-code=${record.taskCode}`
      );
    }

    if (record?.type === "COMMENT") {
      navigate(
        `/work-management/timeline/${record.projectCode}?task-code=${record.taskCode}&comment-code=${record.taskActivityCode}&type=COMMENT`
      );
    }
  };

  const handleProjectNameClickProjectDetail = (record) => {
    navigate(`/work-management/project/${record.projectCode}`);
  };
  const getDataIndexByLanguage = (userLanguage) => {
    switch (userLanguage) {
      case "us":
        return "contentEn";
      case "vn":
        return "contentVn";
      case "kr":
        return "contentKr";
      default:
        return "contentEn";
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: "instant", block: "start" });
    }
  };

  const columns = [
    {
      align: "center",
      title: "",
      dataIndex: "color",
      key: "color",
      render: (_, record) => {
        if (record.type === "PROJECT") {
          return <Icon component={ProjectIcon} className="text-[24px]" />;
        } else if (record.type === "TASK") {
          return <Icon component={TaskIcon} className="text-[27.5px] " />;
        } else
          return (
            <Icon
              component={CommentIcon}
              className="text-[24px] rounded-[2px]"
            />
          );
      },
      width: 50,
    },
    {
      title: languageMap?.["event.dashboard.alarm.title"] ?? "Task / Project",
      dataIndex: "taskName",
      key: "taskName",
      render: (text, record) => truncateText(text || record?.projectName, 100),
      // width: 300,
    },
    {
      title: languageMap?.["event.dashboard.alarm.content"] ?? "Content",
      dataIndex: getDataIndexByLanguage(user?.language),
      key: getDataIndexByLanguage(user?.language),
    },
    {
      title:
        languageMap?.["event.dashboard.alarm.createdDate"] ?? "Created date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => <span>{formatTime(date)}</span>,
      // width: 200,
    },
    {
      align: "center",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Space size="middle">
          <p
            className={`cursor-pointer ${record?.read === "Y" ? "text-green-500" : "text-red-500"}`}
          >
            {record?.read === "Y" ? (
              <CiRead style={{ fontSize: "25px", color: "green" }} />
            ) : (
              <CiUnread style={{ fontSize: "25px", color: "red" }} />
            )}
          </p>
        </Space>
      ),
      width: 100,
    },
  ];

  const messagesToastTask = {
    vn: "Tác vụ này đã bị xóa!",
    us: "This task was deleted!",
    kr: "이  작업이 삭제되었습니다!",
  };
  const messagesToastProject = {
    vn: "Dự án đã bị xóa!",
    us: "The project has been deleted!",
    kr: "프로젝트가 삭제되었습니다!",
  };

  return (
    <>
      <div
        className="alarm mb-[50px]"
        ref={listRef}
        style={{ border: isMobile ? "none" : "" }}
      >
        <div
          className={`my-alarm ${isMobile ? "fixed-header" : ""}`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div className="flex justify-between gap-[10px]">
            {backBtn}
            <div className="font-semibold">
              {languageMap?.["event.dashboard.alarm"] ?? "Alarm"}
            </div>
            <ReloadOutlined
              className="text-[20px] p-[5px]"
              onClick={handleReload}
            />
          </div>
          <div className="search-condition flex">
            <Select
              className="select-dropdown"
              value={dropListSearch.type}
              style={{ width: isMobile ? "100%" : 200 }}
              onChange={handleSelectChange}
              options={[
                {
                  label: languageMap?.["event.dashboard.alarm.all"] ?? "ALL",
                  value: "ALL",
                },
                {
                  label:
                    languageMap?.["event.dashboard.alarm.unread"] ?? "Unread",
                  value: "UNREAD",
                },
                {
                  label: languageMap?.["event.dashboard.alarm.read"] ?? "Read",
                  value: "READ",
                },
              ]}
            ></Select>
            <Tooltip
              placement="top"
              title={
                languageMap?.["event.dashboard.alarm.readAll"] ?? "Read all"
              }
            >
              <CiCircleCheck
                size={30}
                className="cursor-pointer"
                onClick={() => setIsOpenDeleteModal(true)}
              />
            </Tooltip>
          </div>
        </div>

        <div style={{ marginTop: isMobile ? "90px" : "" }}>
          {isMobile ? (
            <Row gutter={[16, 16]}>
              {alarmList?.map((alarmItem) => (
                <Col
                  key={alarmItem?.alarmCode}
                  xs={24}
                  sm={12}
                  md={8}
                  lg={6}
                  xl={4}
                  className=""
                >
                  <Card
                    title={
                      <div className="flex items-center gap-2 ">
                        {alarmItem?.type === "PROJECT" ? (
                          <Icon
                            component={ProjectIcon}
                            className="text-[24px] mr-2"
                          />
                        ) : alarmItem?.type === "TASK" ? (
                          <Icon
                            component={TaskIcon}
                            className="text-[27.5px] mr-2"
                          />
                        ) : (
                          <Icon
                            component={CommentIcon}
                            className="text-[24px] mr-2"
                          />
                        )}
                        <span
                          className="whitespace-pre-wrap"
                          style={{ color: alarmItem?.color }}
                        >
                          {alarmItem?.projectName}
                        </span>
                      </div>
                    }
                    bordered={false}
                    className="shadow-md rounded"
                    style={{ border: `1px solid #cccccc5e` }}
                    extra={
                      alarmItem?.read === "Y" ? (
                        <CiRead style={{ fontSize: "25px", color: "green" }} />
                      ) : (
                        <CiUnread style={{ fontSize: "25px", color: "red" }} />
                      )
                    }
                  >
                    <div className="p-[10px]">
                      <p className="mb-[2px]">
                        {alarmItem[`${getDataIndexByLanguage(user?.language)}`]}
                      </p>
                      <p style={{ color: "#959494" }}>
                        {formatTime(alarmItem?.createdAt)}
                      </p>
                      <div className="mt-[10px] flex justify-between gap-[5px]">
                        <Button
                          className="p-[4px]"
                          type="primary"
                          block
                          onClick={(e) => {
                            e.stopPropagation();
                            if (alarmItem?.read === "N") {
                              handleChangeRead(alarmItem);
                            }

                            if (alarmItem?.taskStatus === "N") {
                              openNotificationWarn(
                                messagesToastTask[user?.language] ||
                                  messagesToastTask["us"]
                              );
                            } else if (alarmItem?.taskCode) {
                              handleTimelineTaskCodeClick(alarmItem);
                            } else if (alarmItem?.projectStatus === "Y") {
                              handleProjectNameClickProjectDetail(alarmItem);
                            } else if (alarmItem?.projectStatus === "N") {
                              openNotificationWarn(
                                messagesToastProject[user?.language] ||
                                  messagesToastProject["us"]
                              );
                            }
                          }}
                        >
                          {languageMap?.["alarm.mobile.button.viewDetails"] ?? "View Details"}
                        </Button>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}

              <div className="flex justify-center mt-4 pagination shadow-md">
                <Pagination
                  current={pagination?.current}
                  pageSize={pagination?.pageSize}
                  total={pagination?.total}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                />
              </div>
            </Row>
          ) : (
            <Table
              className="custom-table-scroll"
              columns={columns}
              dataSource={alarmList}
              pagination={{
                ...pagination,
                showSizeChanger: false,
              }}
              size={"middle"}
              loading={loading}
              onChange={handleTableChange}
              rowKey={(record) => record.key}
              scroll={{
                x: 700,
                // y: 180,
              }}
              onRow={(record) => ({
                style: {
                  height: "50px",
                  cursor: "pointer",
                },
                onClick: () => {
                  if (record?.read === "N") {
                    handleChangeRead(record);
                  }

                  if (record?.taskStatus === "N") {
                    openNotificationWarn(
                      messagesToastTask[user?.language] ||
                        messagesToastTask["us"]
                    );
                  } else if (record?.taskCode) {
                    handleTimelineTaskCodeClick(record);
                  } else if (record?.projectStatus === "Y") {
                    handleProjectNameClickProjectDetail(record);
                  } else if (record?.projectStatus === "N") {
                    openNotificationWarn(
                      messagesToastProject[user?.language] ||
                        messagesToastProject["us"]
                    );
                  }
                },
              })}
              rowClassName="dashboard-row"
            />
          )}
        </div>
      </div>
      {isOpenDeleteModal && (
        <GeneralModal
          title={
            languageMap?.["event.dashboard.alarm.readAll.noti"] ??
            "Do you want to mark all as read"
          }
          onCancel={() => setIsOpenDeleteModal(false)}
          open={isOpenDeleteModal}
          onConfirm={handleChangeAlarmList}
        />
      )}
    </>
  );
};
export { AlarmList };
